'use client';

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
  Dispatch,
} from 'react';
import useSWR, { mutate } from 'swr';
import customFetch, { researchFetch } from '@/lib/utils/fetch';
import { useEventEmitter } from '@/hooks/useEventEmitter';
import { QueryItem, ResearchHistoryResponse } from '@/type/research';
import { AgentMode, ChatMode, useChatMode } from './ChatModeContext';

interface Message {
  role: string;
  content: string;
  createdAt?: string;
  sources?: any[];
}

export interface Chat {
  id: string;
  chatId?: string;
  title: string;
  createdAt: string;
  updatedAt?: string;
  messages?: Message[];
  focusMode?: string;
  category?: string;
  projectId?: string;
  mode?: ChatMode;
  type?: 'team' | 'personal';
  source?: string;
  agent?: AgentMode;
  tags?: string[];
  isArchived?: boolean;
  [k: string]: any;
}

interface ChatContextType {
  // 所有聊天列表
  chats: Chat[];
  // 当前选中的聊天详情
  currentChat: Chat | null;
  // 加载状态
  isLoadingChats: boolean;
  isLoadingCurrentChat: boolean;
  // 错误状态
  chatsError: any;
  currentChatError: any;
  // 操作方法
  fetchChats: () => Promise<void>;
  deleteChat: (chatId: string, type: ChatMode) => Promise<void>;
  pinChat: (chatId: string, type: ChatMode) => Promise<void>;
  unpinChat: (chatId: string, type: ChatMode, tag: string) => Promise<void>;
  archiveChat: (chatId: string, type: ChatMode) => Promise<void>;
  unarchiveChat: (chatId: string, type: ChatMode) => Promise<void>;
  // 辅助方法
  getChatById: (chatId: string) => Chat | undefined;
  refreshChats: () => void;
  refreshCurrentChat: () => void;
  clearCurrentChat: () => void;
  // 分页
  setPage: Dispatch<React.SetStateAction<number>>;
  hasMore: boolean;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChats = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChats must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

const PAGE_SIZE = 100;

const researchHistory2Chats = (data: ResearchHistoryResponse): Chat[] => {
  if (!data?.data) return [];
  return data.data.map((item: QueryItem) => {
    const isRead = !item.searches?.find(
      (item: { readStatus: string }) => item.readStatus !== 'read',
    );
    return {
      id: item.id,
      chatId: item.id,
      title: item.title,
      createdAt: item.createdAt,
      mode: 'research',
      agent: (item.agent ?? 'research') as AgentMode,
      status: item.status,
      isRead: isRead,
      tags: item.isPinned ? ['pin'] : [],
      source: item.source,
      // updatedAt: item.updatedAt,
      // messages: item.messages,
      // focusMode: item.focusMode,
      // category: item.category,
      // projectId: item.projectId,
    };
  });
};

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { isWorkspace, workspaceId, mode } = useChatMode();
  const { data: apiAccessToken } = useSWR('apiAccessToken');
  const { data: researchAccessToken } = useSWR('researchAccessToken');
  const [page, setPage] = useState(1);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [currentChat, setCurrentChat] = useState<Chat | null>(null);
  const emitter = useEventEmitter();
  const type = isWorkspace ? 'team' : 'personal';
  const isEmpty = isWorkspace && !workspaceId;

  const query = `?type=${type}&limit=${PAGE_SIZE}&offset=${(page - 1) * PAGE_SIZE}${
    isWorkspace && workspaceId ? `&teamId=${workspaceId}` : ''
  }`;

  // 获取所有聊天列表
  const {
    data: chatsData,
    error: chatsError,
    isLoading: isLoadingChats,
    mutate: mutateChats,
  } = useSWR(
    apiAccessToken ? (mode === 'predic' ? 'api:/predic/chat' : `api:/chats${query}`) : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      isPaused: () => isWorkspace && !workspaceId,
    },
  );

  // 获取所有聊天列表
  const {
    data: researchData,
    error: researchError,
    isLoading: isLoadingResearch,
    mutate: mutateResearch,
  } = useSWR(
    researchAccessToken && mode !== 'predic' ? `research:/conversations/history${query}` : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      refreshInterval: (data) => {
        const hasRead = (data?.data ?? []).every((item: { searches: any[]; status: string }) => {
          const unRead = !!item.searches?.find(
            (item: { readStatus: string }) => item.readStatus !== 'read',
          );
          if (item.status === 'COMPLETED') {
            return !unRead;
          }
          return false;
        });
        return hasRead ? 0 : 10000;
      },
    },
  );

  const chats = React.useMemo(() => {
    if (isEmpty) {
      return [];
    }
    const chatList = [...(chatsData?.chats || []), ...researchHistory2Chats(researchData)];
    // 按创建时间降序排序
    return [...chatList].sort((a: Chat, b: Chat) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [isEmpty, chatsData?.chats, researchData]);

  const hasMore = React.useMemo(() => {
    return researchData?.pagination?.page?.hasMore || (chatsData?.hasMore ?? false);
  }, [chatsData, researchData]);

  // 获取当前聊天详情
  const {
    data: currentChatData,
    error: currentChatError,
    isLoading: isLoadingCurrentChat,
    mutate: mutateCurrentChat,
  } = useSWR(currentChatId ? `api:/chats/${currentChatId}` : null, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    isPaused: () => isWorkspace && !workspaceId,
  });

  useEffect(() => {
    if (currentChatData?.chat) {
      setCurrentChat(currentChatData.chat);
      // 同时更新聊天列表中的对应项
      if (chats.length > 0) {
        const updatedChats = chats.map((c: Chat) =>
          c.id === currentChatData.chat.id || c.chatId === currentChatData.chat.id
            ? currentChatData.chat
            : c,
        );
        mutate('api:/chats', { chats: updatedChats }, false);
      }
    }
  }, [currentChatData, chats]);

  // 监听聊天删除事件
  useEffect(() => {
    const handleChatDeleted = (chatId: string) => {
      // 从列表中移除被删除的聊天
      mutateChats(
        (data: any) => ({
          ...data,
          chats: data?.chats?.filter((c: Chat) => c.id !== chatId && c.chatId !== chatId) || [],
        }),
        false,
      );
      mutateResearch(
        (data: any) => ({
          ...data,
          chats: data?.chats?.filter((c: Chat) => c.id !== chatId && c.chatId !== chatId) || [],
        }),
        false,
      );

      // 如果删除的是当前聊天，清除当前聊天
      if (currentChatId === chatId) {
        setCurrentChat(null);
        setCurrentChatId(null);
      }
    };

    emitter.on('chatDeleted', handleChatDeleted);
    return () => {
      emitter.off('chatDeleted', handleChatDeleted);
    };
  }, [currentChatId, emitter, mutateChats, mutateResearch]);

  // 获取所有聊天
  const fetchChats = useCallback(async () => {
    await mutateChats();
    await mutateResearch();
  }, [mutateChats, mutateResearch]);

  // 删除聊天
  const deleteChat = useCallback(
    async (chatId: string, type: ChatMode) => {
      const _fetch = type === 'alfa' ? customFetch : researchFetch;
      const url = type === 'alfa' ? `/api/chats/${chatId}` : `api/conversations/${chatId}`;
      const response = await _fetch(url, {
        method: 'DELETE',
      });

      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to delete chat');
      }

      // 触发删除事件
      emitter.emit('chatDeleted', chatId);

      // 刷新列表
      await mutateChats();
      await mutateResearch();

      if (currentChatId === chatId) {
        setCurrentChat(null);
        setCurrentChatId(null);
      }
    },
    [mutateChats, mutateResearch, currentChatId, emitter],
  );

  // 置顶聊天
  const pinChat = useCallback(
    async (chatId: string, type: ChatMode) => {
      const _fetch = type === 'alfa' ? customFetch : researchFetch;
      const url = type === 'alfa' ? `/api/chats/${chatId}/tags` : `api/conversations/${chatId}/pin`;
      const response = await _fetch(url, {
        method: 'POST',
        body: JSON.stringify({ tag: 'pin' }),
      });

      if (!response.ok) {
        throw new Error('Failed to pin chat');
      }
      // 刷新列表
      await mutateChats();
    },
    [mutateChats],
  );

  const unpinChat = useCallback(
    async (chatId: string, type: ChatMode, tag: string) => {
      const _fetch = type === 'alfa' ? customFetch : researchFetch;
      const url =
        type === 'alfa'
          ? `/api/chats/${chatId}/tags?tag=${tag}`
          : `api/conversations/${chatId}/unpin`;
      const response = await _fetch(url, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to unpin chat');
      }
      // 刷新列表
      await mutateChats();
    },
    [mutateChats],
  );

  const archiveChat = useCallback(
    async (chatId: string, type: ChatMode) => {
      const _fetch = type === 'alfa' ? customFetch : researchFetch;
      const url =
        type === 'alfa' ? `/api/chats/${chatId}/tags` : `api/conversations/${chatId}/archive`;
      const response = await _fetch(url, {
        method: 'POST',
        body: JSON.stringify({ tag: 'archive' }),
      });

      if (!response.ok) {
        throw new Error('Failed to archive chat');
      }
      // 刷新列表
      mutateChats();
      mutateResearch();
    },
    [mutateChats, mutateResearch],
  );

  const unarchiveChat = useCallback(
    async (chatId: string, type: ChatMode) => {
      const _fetch = type === 'alfa' ? customFetch : researchFetch;
      const url =
        type === 'alfa'
          ? `/api/chats/${chatId}/tags?tag=archive`
          : `api/conversations/${chatId}/unarchive`;
      const response = await _fetch(url, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to unarchive chat');
      }
      // 刷新列表
      await mutateChats();
    },
    [mutateChats],
  );

  // 根据ID获取聊天（从缓存）
  const getChatById = useCallback(
    (chatId: string) => {
      return chats.find((c: Chat) => c.id === chatId || c.chatId === chatId);
    },
    [chats],
  );

  // 刷新聊天列表
  const refreshChats = useCallback(() => {
    mutateChats();
    mutateResearch();
  }, [mutateChats, mutateResearch]);

  // 刷新当前聊天
  const refreshCurrentChat = useCallback(() => {
    if (currentChatId) {
      mutateCurrentChat();
    }
  }, [currentChatId, mutateCurrentChat]);

  // 清除当前聊天
  const clearCurrentChat = useCallback(() => {
    setCurrentChat(null);
    setCurrentChatId(null);
  }, []);

  const value: ChatContextType = {
    chats,
    currentChat,
    isLoadingChats: isLoadingChats || isLoadingResearch,
    isLoadingCurrentChat,
    chatsError: chatsError || researchError,
    currentChatError,
    fetchChats,
    deleteChat,
    pinChat,
    unpinChat,
    archiveChat,
    unarchiveChat,
    getChatById,
    refreshChats,
    refreshCurrentChat,
    clearCurrentChat,
    setPage,
    hasMore,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
